import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ChatCommandInput =
  | { type: 'send'; message: string; filePaths?: string[]; clearOnAck?: boolean }
  | { type: 'stop' };

export type ChatCommand = ChatCommandInput & { ack: () => void };

interface SelectionRange {
  start: number;
  end: number;
}

interface ChatInputContextValue {
  message: string;
  setMessage: (v: string) => void;
  push: (command: ChatCommandInput) => void;
  queue: ChatCommand[];
  consume: () => ChatCommand | null;
  selectionRef: React.MutableRefObject<SelectionRange>;
}

const ChatInputContext = createContext<ChatInputContextValue | null>(null);

let _initialQuery: string | null | undefined;

const getInitialQuery = () => {
  if (_initialQuery !== undefined) return _initialQuery;
  const url = new URL(window.location.href);
  _initialQuery = url.searchParams.get('q');
  if (_initialQuery) {
    url.searchParams.delete('q');
    window.history.replaceState(null, '', url);
  }
  return _initialQuery;
};

export const ChatInputProvider = ({ children }: { children: ReactNode }) => {
  const [message, setMessage] = useState('');
  const [queue, setQueue] = useState<ChatCommand[]>([]);
  const queueRef = useRef(queue);
  queueRef.current = queue;

  const push = useCallback((command: ChatCommandInput) => {
    const ack = () => {
      if (command.type === 'send' && command.clearOnAck !== false) {
        setMessage('');
      }
    };
    setQueue((prev) => [...prev, { ...command, ack }]);
  }, []);

  const consume = useCallback(() => {
    const q = queueRef.current;
    if (q.length === 0) return null;
    const [first, ...rest] = q;
    queueRef.current = rest;
    setQueue(rest);
    return first;
  }, []);

  const selectionRef = useRef<SelectionRange>({ start: 0, end: 0 }) as React.MutableRefObject<SelectionRange>;

  const didPushQuery = useRef(false);
  useEffect(() => {
    if (didPushQuery.current) return;
    const q = getInitialQuery();
    if (q) {
      didPushQuery.current = true;
      setMessage(q);
      push({ type: 'send', message: q, clearOnAck: false });
    }
  }, [push]);

  return (
    <ChatInputContext.Provider value={{ message, setMessage, push, queue, consume, selectionRef }}>
      {children}
    </ChatInputContext.Provider>
  );
};

export const useChatInputContext = () => {
  const ctx = useContext(ChatInputContext);
  if (!ctx) throw new Error('useChatInputContext must be used within a ChatInputProvider');
  return ctx;
};
